import { Controller, useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { DualListFieldProps } from "./DualList.types";
import DualList from "./DualList";

/**
 * DualListField - A React Hook Form wrapper for the DualList component.
 *
 * This component wraps the DualList controlled component with React Hook Form's
 * Controller, providing seamless integration with RHF's form management.
 *
 * This should ONLY be used within a React Hook Form context (inside a FormProvider
 * or component that uses useForm). For standalone usage without RHF, use the
 * DualList component directly.
 *
 * @example
 * // With React Hook Form
 * <FormProvider {...methods}>
 *   <DualListField name="selectedItems" options={options} required />
 * </FormProvider>
 *
 * @example
 * // Standalone usage (NOT with this component)
 * const [selected, setSelected] = useState<string[]>([]);
 * <DualList options={options} value={selected} onChange={setSelected} />
 */
function DualListField(props: DualListFieldProps) {
  const { name = "", required, validations, defaultValue, shouldUnregister, ...dualListProps } = props;
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      rules={{ required, ...validations }}
      name={name}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      shouldUnregister={shouldUnregister}
      render={({ field, fieldState }) => (
        <div>
          <DualList
            {...dualListProps}
            value={Array.isArray(field.value) ? field.value : []}
            onChange={field.onChange}
            error={!!fieldState.error}
          />
          {fieldState.error && (
            <div className="text-sm error-text text-error-primary mt-sm">
              {fieldState.error?.message ? (
                fieldState.error.message
              ) : (
                <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
              )}
            </div>
          )}
        </div>
      )}
    />
  );
}

export default DualListField;

DualListField.defaultProps = {
  name: "",
  required: false,
  validations: undefined,
  shouldUnregister: false,
  defaultValue: undefined,
};
